import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import AdminBackLink from '../components/AdminBackLink.jsx';
import AdminDateRangeFilter from '../components/AdminDateRangeFilter.jsx';
import { PageHeader, FilterBar, EmptyState, StatusChip, DataTableCard } from '../components/ui';
import { adminAPI } from '../services/api';
import { dateInputValue, normalizeDateInput } from '../utils/adminDateUtils';
import './ModuleAdmin.css';

function defaultTrendingStartDate() {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  return dateInputValue(d);
}

function defaultTrendingEndDate() {
  return dateInputValue(new Date());
}

function formatUpdatedAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatRangeDisplay(start, end) {
  if (!start || !end) return 'Last 15 Days';
  const fmt = (iso) => {
    const [y, m, day] = iso.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

const thSx = {
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#475569',
  bgcolor: '#f8fafc',
  whiteSpace: 'nowrap'
};

const TrendingEvents = () => {
  const [startDate, setStartDate] = useState(defaultTrendingStartDate);
  const [endDate, setEndDate] = useState(defaultTrendingEndDate);
  const startDateQuery = normalizeDateInput(startDate);
  const endDateQuery = normalizeDateInput(endDate);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-trek-detail-clicks', startDateQuery, endDateQuery],
    queryFn: () =>
      adminAPI.getTrekDetailClickLeaderboard({
        limit: 500,
        ...(startDateQuery ? { startDate: startDateQuery } : {}),
        ...(endDateQuery ? { endDate: endDateQuery } : {})
      }),
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });

  const payload = data?.data?.data ?? data?.data ?? [];
  const rows = Array.isArray(payload) ? payload : [];
  const meta = data?.data?.meta ?? {};
  const rangeLabel =
    meta.rangeLabel ||
    (startDateQuery && endDateQuery ? `${startDateQuery} to ${endDateQuery}` : 'Last 15 Days');
  const rangeDisplay = formatRangeDisplay(startDateQuery, endDateQuery);
  const totalClicks = rows.reduce((sum, r) => sum + Number(r.clicks || 0), 0);

  const resetToLast15Days = () => {
    setStartDate(defaultTrendingStartDate());
    setEndDate(defaultTrendingEndDate());
  };

  return (
    <div className="module-page">
      <AdminBackLink to="/" label="Back to Dashboard" />

      <PageHeader
        title="Trending Events"
        subtitle="More details taps on the public Events page. Data is kept permanently — pick a date range to review past trending. The public site still ranks treks using the last 15 days only."
      />

      <FilterBar contentSx={{ py: 2 }}>
        <AdminDateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          presetLabel="Last 15 Days"
          onPresetClick={resetToLast15Days}
        />
      </FilterBar>

      <Card sx={{ mb: 2, px: 2.5, py: 1.75 }}>
        <Typography variant="subtitle1" fontWeight={700} component="h2">
          Click Leaderboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {rangeDisplay}
          {!isLoading && !error ? ` · ${rows.length} treks · ${totalClicks.toLocaleString('en-IN')} clicks` : ''}
        </Typography>
      </Card>

      <DataTableCard>
        {isLoading ? (
          <div className="ops-loading-box">
            <CircularProgress size={32} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">Loading click data…</Typography>
          </div>
        ) : error ? (
          <EmptyState
            title="Could not load click data"
            description="Try again later or pick a different date range."
            sx={{ borderColor: 'error.light' }}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No detail clicks in this range"
            description={`No clicks recorded for ${rangeLabel}. Try wider dates or confirm visitors used More details on the Events page.`}
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, width: 48, textAlign: 'center' }}>#</TableCell>
                <TableCell sx={thSx}>Trek</TableCell>
                <TableCell sx={thSx}>Slug</TableCell>
                <TableCell align="right" sx={thSx}>Clicks</TableCell>
                <TableCell sx={thSx}>Last Click</TableCell>
                <TableCell sx={thSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const trek = row.trek || {};
                const name = trek.name || `Trek #${row.trekId}`;
                const slug = trek.slug || '—';
                const active = trek.isActive !== false;
                return (
                  <TableRow key={row.trekId} hover>
                    <TableCell sx={{ fontWeight: 700, color: '#94a3b8', textAlign: 'center' }}>{index + 1}</TableCell>
                    <TableCell>
                      <RouterLink
                        to="/treks"
                        state={{ openTrekId: Number(row.trekId) }}
                        style={{ fontWeight: 600, color: '#0f172a', textDecoration: 'none' }}
                      >
                        {name}
                      </RouterLink>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#64748b', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {slug}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {Number(row.clicks || 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                      {formatUpdatedAt(row.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={active ? 'active' : 'inactive'} label={active ? 'Active' : 'Inactive'} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DataTableCard>
    </div>
  );
};

export default TrendingEvents;
